import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ParametrosService } from 'app/services/parametros.service';
import { ProductoService } from 'app/services/producto.service';
import { Tributo } from 'app/models/tributo.models';
import { CreateArticleTaxDto, UpdateArticleTaxDto } from 'app/models/article-tax.models';

// Interfaz para manejar impuestos del producto
export interface ProductTax {
  id?: number; // ID del ArticleTax de la API
  tax: Tributo;
  percentage: number;
  observations?: string;
}

export interface ImpuestosDialogData {
  productTaxes: ProductTax[];
  title: string;
  articleId?: number; // ID del artículo para guardar en la API
  editMode?: boolean; // Indica si estamos en modo edición
  currentTax?: any; // Impuesto actual para editar
  taxIndex?: number; // Índice del impuesto a editar
}

@Component({
  selector: 'app-impuestos-dialog',
  templateUrl: './impuestos-dialog.component.html',
  styleUrls: ['./impuestos-dialog.component.css']
})
export class ImpuestosDialogComponent implements OnInit {

  // Variables para el formulario simple
  allTaxes: Tributo[] = []; // Lista completa de impuestos disponibles
  selectedTax: Tributo | null = null; // Impuesto seleccionado
  taxPercentage: number = 0; // Porcentaje del impuesto
  observations: string = ''; // Observaciones del impuesto
  isEditMode: boolean = false; // Modo edición

  constructor(
    public dialogRef: MatDialogRef<ImpuestosDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ImpuestosDialogData,
    private parametroServicio: ParametrosService,
    private productoService: ProductoService,
    private _snackBar: MatSnackBar
  ) {
    // Verificar si estamos en modo edición
    this.isEditMode = this.data.editMode || false;
    
    // Si estamos editando, cargar los datos actuales (excepto selectedTax que se establece después)
    if (this.isEditMode && this.data.currentTax) {
      this.taxPercentage = this.data.currentTax.percentage;
      this.observations = this.data.currentTax.observations || '';
      // selectedTax se establecerá en setSelectedTaxFromCurrentTax() después de cargar allTaxes
    }
  }

  ngOnInit(): void {
    this.loadTaxes();
  }

  // ===== MÉTODOS PARA MANEJO DE IMPUESTOS =====

  /**
   * Carga la lista de impuestos disponibles desde el servidor
   */
  loadTaxes() {
    this.parametroServicio.getTributos().subscribe({
      next: (data: Tributo[]) => {
        this.allTaxes = data || [];
        console.log('Impuestos cargados:', this.allTaxes.length);
        
        // Si estamos en modo edición y tenemos un impuesto actual, 
        // buscar la referencia correcta en allTaxes
        if (this.isEditMode && this.data.currentTax) {
          this.setSelectedTaxFromCurrentTax();
        }
      },
      error: (error) => {
        console.error('Error al cargar impuestos:', error);
        this.openSnackBar('Error al cargar la lista de impuestos', 'Error');
        this.allTaxes = [];
      }
    });
  }

  /**
   * Función para comparar impuestos en el select
   */
  compareTaxes(tax1: Tributo, tax2: Tributo): boolean {
    return tax1 && tax2 ? tax1.id === tax2.id : tax1 === tax2;
  }

  /**
   * Establece el impuesto seleccionado basado en el impuesto actual en modo edición
   */
  private setSelectedTaxFromCurrentTax() {
    if (this.data.currentTax && this.allTaxes.length > 0) {
      const currentTaxId = this.data.currentTax.tax.id;
      this.selectedTax = this.allTaxes.find(tax => tax.id === currentTaxId) || null;
      console.log('Impuesto seleccionado en modo edición:', this.selectedTax);
    }
  }

  /**
   * Actualiza el porcentaje sugerido cuando se selecciona un impuesto
   */
  onTaxSelected() {
    if (this.selectedTax?.alic && this.selectedTax.alic > 0) {
      this.taxPercentage = this.selectedTax.alic;
    }
  }

  /**
   * TrackBy function para optimizar renderizado de la lista de impuestos
   */
  trackByTaxId(index: number, tax: any): any {
    return tax.id;
  }

  /**
   * TrackBy function para optimizar renderizado de la tabla
   */
  trackByIndex(index: number): number {
    return index;
  }

  /**
   * Guarda el impuesto y cierra el diálogo
   */
  onSave() {
    // Validaciones obligatorias
    if (!this.selectedTax) {
      this.openSnackBar('Debe seleccionar un tipo de impuesto', 'Error');
      return;
    }

    if (!this.taxPercentage || this.taxPercentage <= 0) {
      this.openSnackBar('Debe especificar un porcentaje válido mayor a 0', 'Error');
      return;
    }

    if (this.taxPercentage > 100) {
      this.openSnackBar('El porcentaje no puede ser mayor al 100%', 'Error');
      return;
    }

    if (!this.observations || this.observations.trim() === '') {
      this.openSnackBar('Debe ingresar un nombre para el impuesto', 'Error');
      return;
    }

    // Si tenemos articleId válido, interactuar con la API
    const articleId = Number(this.data.articleId);
    if (articleId && articleId > 0 && !isNaN(articleId)) {
      
      if (this.isEditMode) {
        // ===== MODO EDICIÓN =====
        const updateData: UpdateArticleTaxDto = {
          taxId: Number(this.selectedTax.id),
          taxPercentage: Number(this.taxPercentage),
          name: this.observations.trim()
        };

        // Necesitamos el ID del ArticleTax, no del Tax
        const articleTaxId = this.data.currentTax?.id;
        if (!articleTaxId) {
          this.openSnackBar('Error: No se puede identificar el impuesto a editar', 'Error');
          return;
        }

        console.log('Actualizando impuesto en la API:', updateData);

        this.productoService.updateArticleTax(articleTaxId, updateData).subscribe({
          next: (updatedTax) => {
            this.openSnackBar(`Impuesto "${this.observations.trim()}" actualizado correctamente`, 'Éxito');
            
            // Crear el objeto actualizado para el componente padre
            const updatedTaxObject: ProductTax = {
              tax: this.selectedTax!,
              percentage: this.taxPercentage,
              observations: this.observations.trim()
            };

            setTimeout(() => {
              this.dialogRef.close({
                saved: true,
                updatedTax: updatedTaxObject,
                apiResponse: updatedTax
              });
            }, 500);
          },
          error: (error) => {
            console.error('Error al actualizar impuesto:', error);
            const errorMessage = error.error?.message || error.message || 'Error desconocido';
            this.openSnackBar(`Error al actualizar el impuesto: ${errorMessage}`, 'Error');
          }
        });

      } else {
        // ===== MODO CREACIÓN =====
        const articleTaxData: CreateArticleTaxDto = {
          articleId: articleId,
          taxId: Number(this.selectedTax.id),
          taxPercentage: Number(this.taxPercentage),
          name: this.observations.trim()
        };

        console.log('Enviando impuesto a la API:', articleTaxData);

        this.productoService.addArticleTax(articleTaxData).subscribe({
          next: (savedTax) => {
            this.openSnackBar(`Impuesto "${this.observations.trim()}" guardado correctamente`, 'Éxito');
            
            const newTax: ProductTax = {
              tax: this.selectedTax!,
              percentage: this.taxPercentage,
              observations: this.observations.trim()
            };

            setTimeout(() => {
              this.dialogRef.close({
                saved: true,
                newTax: newTax,
                apiResponse: savedTax
              });
            }, 500);
          },
          error: (error) => {
            console.error('Error al guardar impuesto:', error);
            const errorMessage = error.error?.message || error.message || 'Error desconocido';
            this.openSnackBar(`Error al guardar el impuesto: ${errorMessage}`, 'Error');
          }
        });
      }
      
    } else {
      // Modo sin API (compatibilidad con versión anterior)
      const taxObject: ProductTax = {
        tax: this.selectedTax,
        percentage: this.taxPercentage,
        observations: this.observations.trim()
      };

      const message = this.isEditMode ? 'actualizado' : 'agregado';
      this.openSnackBar(`Impuesto "${this.observations.trim()}" ${message} correctamente`, 'Éxito');

      setTimeout(() => {
        this.dialogRef.close({
          saved: true,
          [this.isEditMode ? 'updatedTax' : 'newTax']: taxObject
        });
      }, 500);
    }
  }

  /**
   * Cancela los cambios y cierra el diálogo
   */
  onCancel() {
    this.dialogRef.close({
      saved: false
    });
  }

  /**
   * Valida que solo se puedan ingresar números y punto decimal (no coma)
   * Limita a máximo 2 dígitos después del punto decimal
   */
  onKeyPress(event: KeyboardEvent) {
    const charCode = event.which ? event.which : event.keyCode;
    const charStr = String.fromCharCode(charCode);
    
    // Permitir: backspace, tab, delete, escape, enter
    if ([8, 9, 27, 13, 46].indexOf(charCode) !== -1 ||
        // Permitir: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        (charCode === 65 && event.ctrlKey === true) ||
        (charCode === 67 && event.ctrlKey === true) ||
        (charCode === 86 && event.ctrlKey === true) ||
        (charCode === 88 && event.ctrlKey === true)) {
      return;
    }
    
    // Permitir solo números (0-9) y punto decimal
    if ((charCode < 48 || charCode > 57) && charCode !== 46) {
      event.preventDefault();
      return;
    }
    
    const currentValue = (event.target as HTMLInputElement).value;
    
    // Prevenir múltiples puntos decimales
    if (charCode === 46 && currentValue.indexOf('.') !== -1) {
      event.preventDefault();
      return;
    }
    
    // Limitar a 2 dígitos después del punto decimal
    const decimalIndex = currentValue.indexOf('.');
    if (decimalIndex !== -1) {
      const decimalPart = currentValue.substring(decimalIndex + 1);
      if (decimalPart.length >= 2 && charCode >= 48 && charCode <= 57) {
        event.preventDefault();
        return;
      }
    }
    
    // Bloquear coma (código 44)
    if (charCode === 44) {
      event.preventDefault();
      return;
    }
  }

  /**
   * Muestra un mensaje usando MatSnackBar
   */
  private openSnackBar(message: string, action: string) {
    this._snackBar.open(message, action, {
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
    });
  }
}